import React, { useEffect, useState } from "react";
import { Box, Typography, Tooltip, Button, TextField, Chip, IconButton } from "@mui/material";
import {
  MutationDraft,
  MutationPlan,
  MutationRequest,
  isMutationPlan,
  isFailedPlacement,
  FailedPlacement,
} from "../../../stores/MutationDraft/types";
import { SmallChip, TherapyChip, VisitTypeBadge } from "../../../atoms/Chip";
import { Close, Check, Visibility, VisibilityOff } from "@mui/icons-material";
import { observer } from "mobx-react";
import useStore from "../../../helpers/useStore";
import {
  MutationDraftContext,
  useMutationScope,
} from "../../../stores/MutationDraft/mutationDraft.provider";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { formatDateUI, formatTime } from "../../../helpers/dateFormat";
import MutationPlanBody from "./MutationPlanBody";
import { useTimeSlotDisplayData } from "../../../stores/Appointment/appointment.queries";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import { getContrastTextColor } from "../../../themes/colorUtils";
import { getColorForRxNumber } from "../../../helpers/statusColor";
import { usePatientFromDraft } from "./usePatientFromDraft";
import { methodColorKey } from "../../../helpers/slotTherapy";

/** Extract timeSlotId from any draft variant */
function getTimeSlotIdFromDraft(draft: MutationDraft | undefined | null): string | undefined {
  if (!draft) return undefined;
  if (isFailedPlacement(draft)) return draft.timeSlotId;
  return draft.placements[0]?.timeSlotId;
}

interface DraftRequestCardProps {
  requestId: string;
  showAcceptReject?: boolean; // default: true
}

const REJECTION_CHIPS = [
  { label: "Nicht verfügbar", icon: "🚫" },
  { label: "Fahrtweg", icon: "🏠" },
  { label: "Therapeut", icon: "👨‍⚕️" },
  { label: "Tag/Uhrzeit", icon: "📅" },
];

// ─── Sub-component for failed placements (reads display data from RQ cache) ──

const FailedPlacementCard: React.FC<{
  fp: FailedPlacement;
}> = ({ fp }) => {
  const { data: displayData } = useTimeSlotDisplayData(fp.timeSlotId, !!fp.timeSlotId);
  const { patient: resolvedPatient } = usePatientFromDraft(fp.timeSlotId, fp.patientId);
  const displayName = resolvedPatient
    ? `${resolvedPatient.firstName} ${resolvedPatient.lastName}`.trim()
    : "";
  const displayTherapy = displayData?.therapy?.abbreviation ? displayData.therapy.abbreviation : "";
  const rxNumber = displayData?.rx?.rxNumber;
  const treatmentIndex = displayData?.treatmentIndex;
  const sublocality = displayData?.sublocality;
  const rxNumberColor = rxNumber ? getColorForRxNumber(rxNumber) : "transparent";
  const treatmentIndexColor = mobiliTheme.palette.info.main;

  return (
    <Box
      sx={{
        p: 0.75,
        mb: 0.75,
        bgcolor: "error.50",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "error.light",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.7rem" }}>
            {/* ⚠️ {[displayName, displayTherapy].filter(Boolean).join(" · ")} */}
            ⚠️ {displayName ?? "Termin"}
          </Typography>
          {rxNumber && (
            <SmallChip
              label={rxNumber}
              sx={{
                fontSize: "0.55rem",
                width: "auto",
                backgroundColor: rxNumberColor,
                color: getContrastTextColor(rxNumberColor),
              }}
            />
          )}
          {treatmentIndex && (
            <SmallChip
              label={treatmentIndex}
              sx={{
                fontSize: "0.55rem",
                width: "auto",
                backgroundColor: treatmentIndexColor,
                color: getContrastTextColor(treatmentIndexColor),
              }}
            />
          )}
          {displayData?.therapy?.abbreviation && (
            <TherapyChip
              therapyName={displayData.therapy.abbreviation}
              colorKey={
                displayData.therapy.therapyMethodId
                  ? methodColorKey(displayData.therapy.therapyMethodId)
                  : undefined
              }
              sx={{ fontSize: "0.55rem", width: "auto" }}
            />
          )}
          {sublocality?.name && (
            <VisitTypeBadge
              visitType={displayData?.address?.addressType || ""}
              visitFee={displayData?.visitFee?.abbreviation}
              sublocality={sublocality}
              sx={{ fontSize: 12 }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          color="error.main"
          sx={{ fontSize: "0.6rem", fontWeight: 600 }}
        >
          Nicht platziert
        </Typography>
      </Box>
      <Typography
        variant="caption"
        color="error.dark"
        sx={{ fontSize: "0.55rem", display: "block", mt: 0.25 }}
      >
        {fp.reason}
      </Typography>
    </Box>
  );
};

const DraftRequestCard: React.FC<DraftRequestCardProps> = observer(
  ({ requestId, showAcceptReject = true }) => {
    const scope = useMutationScope();
    const registry = useStore(MutationDraftContext);
    const draftStore = registry.getStore(scope);
    const calendarStore = useStore(CalendarContext);

    const [rejected, setRejected] = useState(false);
    const [rejectedDraft, setRejectedDraft] = useState<MutationDraft | null>(null);

    const request = draftStore.getRequest(requestId);
    const draft = draftStore.getDraft(requestId);
    const rejectionText = draftStore.getRejection(requestId) ?? "";

    const agentPatientId = draftStore.agent.state?.lastParams?.patientId;
    const { patient } = usePatientFromDraft(undefined, agentPatientId);
    const patientName = patient
      ? `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim() || undefined
      : undefined;
    // const headerLabel = patientName
    //   ? patientName
    //   : draftStore.agent.activeAgentName == "backplanning"
    //     ? request?.dayStr
    //     : "Termin Gruppe";
    const headerLabel = request?.dayStr ? formatDateUI(request.dayStr) : "Termin Gruppe";

    // Auto-close rejection when a new draft arrives (e.g. from resend)
    useEffect(() => {
      if (rejected && draft) {
        setRejected(false);
        setRejectedDraft(null);
      }
    }, [rejected, draft]);

    const handleAccept = () => {
      // Keep draft in store — accept is a visual no-op (save handled externally)
      alert(
        "feature under development: akzeptieren speichert die Platzierung und schließt die Karte",
      );
    };

    const handleReject = () => {
      if (draft) setRejectedDraft(draft);
      setRejected(true);
      draftStore.removeDraft(requestId);
    };

    const handleCancelRejection = () => {
      if (rejectedDraft && request) {
        draftStore.setDraft(requestId, rejectedDraft, request);
      }
      setRejectedDraft(null);
      setRejected(false);
      draftStore.clearRejection(requestId);
    };

    const handleChipClick = (chipLabel: string) => {
      const newVal = rejectionText ? `${rejectionText}, ${chipLabel}` : chipLabel;
      draftStore.setRejection(requestId, newVal);
    };

    // Active or snapshot draft for rendering the card body
    const visibleDraft = draft ?? rejectedDraft;
    const isFailed = visibleDraft ? isFailedPlacement(visibleDraft) : false;

    // Navigate calendar to the placement date
    const placementDateStr = (() => {
      if (!visibleDraft || isFailed) return "";
      if (isMutationPlan(visibleDraft))
        return visibleDraft.placements[0]?.start?.slice(0, 10) ?? "";
      return "";
    })();

    const isViewing = !!placementDateStr && calendarStore.selectedDateStr === placementDateStr;

    const handleNavigateToDate = () => {
      if (placementDateStr) {
        calendarStore.setSelectedDateStr(placementDateStr);
      }
    };

    // Placement time string for header subtitle
    const placementTimeStr = (() => {
      if (!visibleDraft || isFailed) return "";
      if (isMutationPlan(visibleDraft)) {
        const p = visibleDraft.placements[0];
        if (!p) return "";
        return `${formatDateUI(p.start)} ${formatTime(p.start)} – ${formatTime(p.end)}`;
      }
      return "";
    })();

    // ── Failed placement (unplaced) ──
    if (isFailed && isFailedPlacement(visibleDraft!)) {
      const fp = visibleDraft as FailedPlacement;
      return <FailedPlacementCard fp={fp} />;
    }

    // ── No draft yet (waiting for auto-generation) ──
    if (!visibleDraft && !rejected) {
      return (
        <Box sx={{ p: 0.75, mb: 0.75, bgcolor: "grey.50", borderRadius: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              {patientName && (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: "0.7rem", display: "block" }}
                >
                  {patientName}
                </Typography>
              )}
              {/* <Typography variant="caption" color="text.secondary">
                {therapies}
              </Typography> */}
            </Box>
            <Typography variant="caption" color="text.disabled">
              Berechne…
            </Typography>
          </Box>
        </Box>
      );
    }

    // ── Card with draft (normal or rejected) ──
    const borderColor = rejected ? "error.light" : isViewing ? "primary.main" : "divider";

    return (
      <Box
        sx={{
          mb: 0.75,
          border: isViewing ? "2px solid" : "1px solid",
          borderColor,
          borderRadius: 1.5,
          overflow: "hidden",
        }}
      >
        {/* Card header — patient · therapy · time */}
        <Box
          sx={{
            px: 1,
            py: 0.5,
            bgcolor: rejected ? "error.50" : "grey.50",
            borderBottom: "1px solid",
            borderColor,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, fontSize: "0.7rem", display: "block" }}
            >
              {rejected ? "❌ " : "📌 "}
              {headerLabel}
            </Typography>
            {/* {placementTimeStr && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                {placementTimeStr}
              </Typography>
            )} */}
          </Box>
          {!rejected && visibleDraft && (
            <Tooltip title="Im Kalender anzeigen">
              <IconButton size="small" onClick={handleNavigateToDate} sx={{ p: 0.25 }}>
                {isViewing ? (
                  <Visibility sx={{ fontSize: 14, color: "primary.main" }} />
                ) : (
                  <VisibilityOff sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {rejected && (
            <Tooltip title="Ablehnung aufheben">
              <IconButton size="small" onClick={handleCancelRejection} sx={{ p: 0.25 }}>
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Card body — shifts + placement (always visible) */}
        {visibleDraft && !isFailed && (
          <Box sx={{ p: 1, opacity: rejected ? 0.6 : 1 }}>
            <MutationPlanBody
              draft={visibleDraft as MutationPlan}
              dayStr={request?.dayStr}
              showPatientName={draftStore.agent.activeAgentName === "backplanning"}
            />
          </Box>
        )}

        {/* Rejection section — inline below the plan */}
        {rejected && (
          <Box sx={{ px: 1, pb: 1, borderTop: "1px dashed", borderColor: "error.light" }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6rem",
                fontWeight: 600,
                color: "error.dark",
                display: "block",
                mt: 0.75,
                mb: 0.25,
              }}
            >
              ABLEHNUNG:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
              {REJECTION_CHIPS.map((chip) => (
                <Chip
                  key={chip.label}
                  label={`${chip.icon} ${chip.label}`}
                  size="small"
                  onClick={() => handleChipClick(chip.label)}
                  sx={{
                    fontSize: "0.6rem",
                    height: 22,
                    bgcolor: "error.50",
                    color: "error.dark",
                    "&:hover": { bgcolor: "error.main", color: "#fff" },
                  }}
                />
              ))}
            </Box>
            <TextField
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              size="small"
              placeholder="Begründung…"
              value={rejectionText}
              onChange={(e) => draftStore.setRejection(requestId, e.target.value)}
              sx={{ "& .MuiInputBase-input": { fontSize: "0.7rem" } }}
            />
          </Box>
        )}

        {/* Card actions — only when not rejected */}
        {!rejected && showAcceptReject && (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<Check sx={{ fontSize: 14 }} />}
              onClick={handleAccept}
              sx={{ fontSize: "0.6rem", py: 0.15, minWidth: 0, textTransform: "none", flex: 1 }}
            >
              Annehmen
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<Close sx={{ fontSize: 14 }} />}
              onClick={handleReject}
              sx={{ fontSize: "0.6rem", py: 0.15, minWidth: 0, textTransform: "none", flex: 1 }}
            >
              Ablehnen
            </Button>
          </Box>
        )}
      </Box>
    );
  },
);

export default DraftRequestCard;
